// Advisory mirror of service-common/lib/python/tools/plan_export_cost.py. The server enforces the
// budget; this exists so the wizard can show an estimate without a round trip per click.
// Constants are Lambda-measured — do not substitute figures measured on a developer machine.
export const EXPANDED_PAGES = 0.39;
export const COMPACT_PAGES = 0.078;
export const CHIP_PAGES = 0.03;
export const PAGE_BUDGET = 900;
export const DEFAULT_OVERLAP = 0.94;

/**
 * Round half-to-even (banker's rounding), matching Python's built-in `round()`.
 * `Math.round()` is half-up and disagrees with Python on exact `.5` ties, which
 * can flip the `overBudget` verdict at the tie point. Only ties (fractional part
 * exactly 0.5) need special handling; everything else matches `Math.round()`.
 *
 * @param {number} n
 * @returns {number}
 */
export function roundHalfEven(n) {
  const floor = Math.floor(n);
  const diff = n - floor;
  if (diff < 0.5) return floor;
  if (diff > 0.5) return floor + 1;
  // Exact tie: round to the nearest even integer.
  return floor % 2 === 0 ? floor : floor + 1;
}

/**
 * Estimate rendered pages for a selection of versions.
 *
 * @param {Array<{versionId?: string, items?: number}>} versions
 * @param {number} expandedShare fraction of unique items rendered as full blocks (0..1)
 * @param {number} [overlap] fraction of an additional version's items assumed to already be
 *   covered by the largest version
 * @returns {{chips: number, unique: number, expanded: number, compact: number, pages: number, overBudget: boolean}}
 */
export function estimatePages(versions, expandedShare, overlap = DEFAULT_OVERLAP) {
  // Python does `int(v.get("items") or 0)`, which truncates toward zero (not
  // floor - differs from floor for negatives). `Number(...) || 0` deliberately
  // does NOT mirror Python's `int("abc")` raising: a non-numeric value silently
  // becomes 0 here, which is the better behavior for advisory UI code, and the
  // reviewer explicitly asked to leave that divergence alone.
  const sizes = (versions || []).map(v => Math.trunc(Number(v.items) || 0)).sort((a, b) => b - a);

  if (!sizes.length) {
    return { chips: 0, unique: 0, expanded: 0, compact: 0, pages: 0, overBudget: false };
  }

  // Every version indexes its own items, so chips are NOT deduplicated.
  const chips = sizes.reduce((sum, s) => sum + s, 0);

  // A union is never smaller than its largest member. The largest version counts in full;
  // each additional one contributes only its non-overlapping share. A `sum * factor` formula
  // would make adding a version shrink the estimate, which is how the first prototype
  // reported 709 -> 604 pages.
  const unique = roundHalfEven(sizes[0] + sizes.slice(1).reduce((sum, s) => sum + s * (1.0 - overlap), 0));

  const share = Math.min(Math.max(Number(expandedShare) || 0, 0), 1);
  const expanded = roundHalfEven(unique * share);
  const compact = unique - expanded;

  const pages = roundHalfEven(expanded * EXPANDED_PAGES + compact * COMPACT_PAGES + chips * CHIP_PAGES);

  return { chips, unique, expanded, compact, pages, overBudget: pages > PAGE_BUDGET };
}
